import type { FigmaRestApi, ZeytalFigma, ZeytalTokens } from "@zeytal/types";

import { getStyleImplementationKeyRegExp } from "../utils/figma-data.ts";

const mapStyles = (
  styles: Record<string, FigmaRestApi.Style>,
  nodes: ZeytalTokens.LeafNode[],
) => {
  return Object.entries(styles)
    .filter(([, { remote }]) => !remote)
    .reduce<ZeytalTokens.MappedStyle[]>((acc, [key, values]) => {
      const styleImplementationKeyRegExp = getStyleImplementationKeyRegExp(
        values.styleType,
      );

      // Find a node implementing the style
      const [implementationNode] = nodes.filter(({ styles }) => {
        const foundImplementations = Object.entries(styles).filter(
          ([sType, sKey]) =>
            sKey === key && styleImplementationKeyRegExp.test(sType),
        );
        return !!foundImplementations.length;
      });

      // Skip the style since it hasn't been implemented
      if (!implementationNode) {
        return acc;
      }

      return [
        ...acc,
        {
          ...values,
          key,
          node: implementationNode,
        },
      ];
    }, []);
};

// Removing conflicting styles based on their names.
const getUniqueStyles = (mappedStyles: ZeytalTokens.MappedStyle[]) =>
  mappedStyles.reduce<ZeytalTokens.MappedStyle[]>((acc, current) => {
    const x = acc.find((item) => item.name === current.name);

    if (!x) {
      return acc.concat([current]);
    }

    return acc;
  }, []);

export const getMappedStyles = (
  figmaFiles: ZeytalFigma.MinimalFigmaFile[],
  nodes: ZeytalTokens.LeafNode[],
) => {
  const allMappedStyles = figmaFiles.reduce<ZeytalTokens.MappedStyle[]>(
    (acc, file) => {
      const { styles } = file;

      let mappedStyles = [] as ZeytalTokens.MappedStyle[];

      if (!styles) {
        return acc;
      }

      mappedStyles = mapStyles(styles, nodes);

      return [...acc, ...mappedStyles];
    },
    [],
  );

  // Removing conflicting styles.
  const uniqueStyles = getUniqueStyles(allMappedStyles);

  return uniqueStyles;
};
